/**
 * Shared failure-announce helper for portal click actions.
 *
 * Extracted from the receipt-shelf pattern (#458 / fix-r18c): every rejected
 * request must tell the operator, and a cancelled/superseded page must not
 * announce a false failure. Success messaging stays with the caller so each
 * action keeps its own voice (quiet refresh, Tag's 409 warnings, etc.).
 *
 * rd R8-12 (round 11): the face of a failed action is ONE decided sentence. It
 * used to be `${actionLabel} failed: ${actionErrorMessage(error)}`, so a page
 * printed "Looks right for 123 Test Street failed: denied (403)" — the action's
 * own name glued to a raw server message or a status code. The server's words
 * are no longer on the face; a page that has a Show details can still show them
 * through `actionErrorMessage` (which still strips hazards).
 *
 * rd round 12, lane A5a: the sentences and the detail accessor now live in
 * `./failure.ts`, the one home (owner decision 1, item A5-2). This module keeps
 * `runAnnouncedAction` for its twenty call sites and re-exports the failure
 * words so no page changes. `runWrite` (item A5-1) is the write primitive a
 * page moves onto in wave B; `runAnnouncedAction` stays as the older, simpler
 * wrapper for the call sites that have not moved.
 */
import { legacyAnnouncedFailureSentence } from "./failure.js";
import { readerRole } from "./facts.js";

export { actionErrorMessage, isUnreachable } from "./failure.js";

/**
 * Run async work; on failure announce into `live` when the page is still
 * current. Returns whether work completed without throwing (and the page is
 * still current).
 *
 * `_actionLabel` stays in the signature so the twenty call sites do not
 * change, but it is not printed: the face is the shared sentence.
 */
export async function runAnnouncedAction(
  live: HTMLElement,
  stillCurrent: () => boolean,
  _actionLabel: string,
  work: () => Promise<void>,
): Promise<boolean> {
  try {
    await work();
    return stillCurrent();
  } catch (error) {
    if (!stillCurrent()) return false;
    live.textContent = failureSentence(error);
    return false;
  }
}

/**
 * The one sentence a person reads when an action did not save.
 *
 * The words come from `ui/failure.ts`; this wrapper supplies the reader's role.
 * Every call site of this helper predates round 12, so the wording is the
 * pre-round-12 view until wave B moves each page onto `failureCopy` /
 * `outcomeSentence` (see `legacyAnnouncedFailureSentence` for why the owner's
 * 403 sentence is not switched here in wave A).
 */
export function failureSentence(error: unknown): string {
  // Owner decision 10: an unknown reader is the account manager, never the
  // owner. This helper has no page role to pass, so it reads the shell's.
  return legacyAnnouncedFailureSentence(error, readerRole());
}
